// Shared modal chrome (MODAL_STANDARDIZATION_PLAN.md). Every modal in the
// app migrates onto ModalShell/ConfirmDialog so headers, close buttons,
// sizing, and keyboard behavior are identical everywhere.
import { useEffect, type ReactNode } from "react";
import { useEscapeToClose } from "../lib/useEscapeToClose.js";

export type ModalSize = "md" | "lg" | "xl" | "2xl";

/** Styling for a set of mutually exclusive choice buttons in a catch-up row
 *  ("Took it" / "Didn't take", "Did it" / "Didn't", ...). Every option in the
 *  set uses this SAME class on purpose: an accent-filled option beside outlined
 *  ones reads as already-selected, which made users think a decision had been
 *  made for them. None of these is a default — the user picks one, or leaves the
 *  row untouched to defer it to the next sweep. */
export const CHOICE_CLASS =
  "rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 " +
  "hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

const SIZE_CLASS: Record<ModalSize, string> = {
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
};

/** Standard modal chrome (MODAL_STANDARDIZATION_PLAN.md).
 *  Phone: full-width bottom sheet. sm+: centered card.
 *  Provides: backdrop click + Escape to close, body scroll lock,
 *  sticky header with 44px close button top-right, scrollable body. */
export function ModalShell({
  title,
  subtitle,
  icon,
  headerActions,
  subheader,
  footer,
  size = "md",
  escapeEnabled = true,
  onClose,
  children,
  bodyClassName,
}: {
  title: ReactNode;
  /** Small gray line under the title (e.g. "computed 5 min ago"). */
  subtitle?: ReactNode;
  /** Leading icon, e.g. <PillIcon size={18} className="text-(--accent)" />. */
  icon?: ReactNode;
  /** Extra header buttons, rendered between the title block and the close button. */
  headerActions?: ReactNode;
  /** Pinned (non-scrolling) row below the header, e.g. a search input. */
  subheader?: ReactNode;
  /** Pinned footer row (action buttons that must stay visible). */
  footer?: ReactNode;
  size?: ModalSize;
  /** Set false while a child ConfirmDialog is open so Escape only closes the child. */
  escapeEnabled?: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Override the scroll-body classes (default "overflow-y-auto p-4"). */
  bodyClassName?: string;
}) {
  useEscapeToClose(onClose, escapeEnabled);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={[
          "fixed z-50 flex flex-col overflow-hidden bg-white shadow-xl dark:bg-gray-900",
          "inset-x-0 bottom-0 max-h-[95dvh] rounded-t-2xl",
          "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2",
          "sm:w-full sm:max-h-[90dvh] sm:rounded-2xl sm:border sm:border-gray-200 sm:dark:border-gray-800",
          SIZE_CLASS[size],
        ].join(" ")}
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          {icon}
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
          </div>
          {headerActions}
          <button
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-base font-bold text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {subheader && (
          <div className="shrink-0 border-b border-gray-100 px-4 py-3 dark:border-gray-800">{subheader}</div>
        )}
        <div className={bodyClassName ?? "overflow-y-auto p-4"}>{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-gray-200 px-4 py-3 dark:border-gray-800">{footer}</div>
        )}
      </div>
    </>
  );
}

/** Compact confirmation dialog (Decision 3). Always centered, even on phones.
 *  Renders above ModalShell (z-60). Escape/backdrop = cancel. */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  message?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Red confirm button for deletes and other irreversible actions. */
  destructive?: boolean;
  /** Disables both buttons while the action is in flight. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEscapeToClose(onCancel);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="w-full max-w-sm space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-800 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {message && <div className="text-sm text-gray-600 dark:text-gray-300">{message}</div>}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="min-h-11 flex-1 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={
              "min-h-11 flex-1 rounded-lg text-sm font-medium disabled:opacity-50 " +
              (destructive
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-(--accent) text-(--accent-contrast) hover:bg-(--accent-hover)")
            }
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
